"use client";

import * as React from "react";
import { ThemeProvider as NextThemesProvider } from "next-themes";
import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  from,
  Observable,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { ApolloProvider } from "@apollo/client/react";
import getBaseUrl from "../../lib/base-url";

/**
 * Clear guest cookie
 * Exported so apps can call this when Clerk authentication is detected
 */
export function clearGuestCookie(): void {
  if (typeof window === "undefined") return;

  // Clear the guest token cookie
  document.cookie =
    "casey_guest_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax";
  console.log("Guest cookie cleared");
}

/**
 * Get guest token from cookie
 */
function getGuestToken(): string | null {
  if (typeof window === "undefined") return null;

  // Check for guest token in cookie
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === "casey_guest_token" && value) {
      return decodeURIComponent(value);
    }
  }

  return null;
}

/**
 * Get authentication token (either Clerk or guest)
 * Priority: Clerk authentication > guest token from cookie
 * Note: This function is called during request setup, so it reads current auth state
 */
function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;

  // Only use guest token if present
  // Clerk authentication is handled server-side via cookies
  return getGuestToken();
}

// Create auth link that adds Authorization header
const authLink = setContext((_, { headers }) => {
  const token = getAuthToken();

  // If we have a token from cookie, it's a guest token
  const isGuest = !!token;

  return {
    headers: {
      ...headers,
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(isGuest ? { "casey-x-guest": "1" } : {}),
    },
  };
});

// Create HTTP link
const httpLink = new HttpLink({
  uri: `${getBaseUrl()}/api/graphql`,
  credentials: "include", // Ensure cookies are sent with requests
});

// Store the getToken function from Clerk for token refresh
let clerkGetToken: (() => Promise<string | null>) | null = null;

/**
 * Set the Clerk getToken function for session refresh
 * This should be called from the app's ClerkProvider context
 */
export function setClerkGetToken(getToken: () => Promise<string | null>): void {
  clerkGetToken = getToken;
}

// Create error link that handles TOKEN_EXPIRED errors
const errorLink = onError(({ graphQLErrors, operation, forward }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      // Check for TOKEN_EXPIRED error code
      if (err.extensions?.code === "TOKEN_EXPIRED") {
        // If we have a Clerk getToken function, refresh the session
        if (clerkGetToken) {
          return new Observable((observer) => {
            clerkGetToken!()
              .then((newToken) => {
                // Token refresh successful - Clerk updates the __session cookie automatically
                // Retry the original request
                const subscriber = {
                  next: observer.next.bind(observer),
                  error: observer.error.bind(observer),
                  complete: observer.complete.bind(observer),
                };
                forward(operation).subscribe(subscriber);
              })
              .catch((error) => {
                console.error("Failed to refresh Clerk session:", error);
                observer.error(error);
              });
          });
        } else {
          // No Clerk context available, redirect to login
          console.warn(
            "TOKEN_EXPIRED received but no Clerk getToken available",
          );
          if (typeof window !== "undefined") {
            window.location.href = "/auth/login";
          }
        }
      }
    }
  }
});

// Create Apollo Client with error link, auth link, and http link
const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
});

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <NextThemesProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
      enableColorScheme
    >
      <ApolloProvider client={client}>{children}</ApolloProvider>
    </NextThemesProvider>
  );
}

// Export ClerkApolloBridge for apps that use Clerk
export { ClerkApolloBridge } from "./clerk-apollo-bridge";
